import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { css } from "styled-components";
/* Custom imports */
import { StyledButton } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { StyledClickable, StyledTypography } from "@insite/mobius/Link";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { COLORS, TYPOGRAPHY } from "@store/theme/design";
import { linkStyles, siteWidthStyles } from "@store/theme/mixins";

interface OwnProps extends WidgetProps {
    extendedStyles?: FooterContainerStyles;
}

export interface FooterContainerStyles {
    widgetGridContainer?: GridContainerProps;
    widgetBottomGridContainer?: GridContainerProps; // Store Custom Code
    logoGridItem?: GridItemProps;
    linkList1GridItem?: GridItemProps;
    linkList2GridItem?: GridItemProps;
    linkList3GridItem?: GridItemProps;
    linkList4GridItem?: GridItemProps;
    subscribeGridItem?: GridItemProps;
    siteMapLinksGridItem?: GridItemProps;
    richContentGridItem?: GridItemProps;
    logoWrapper?: InjectableCss;
}

// Store Custom Code - Custom Styles
const footerLinkListStyles = (mobileAccordion = false) => {
    return css`
        ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    css``,
                    css`
                        margin-bottom: -1px;
                        padding-top: 0;
                        padding-bottom: 0;

                        > div {
                            border-top: 1px solid ${COLORS.text.light};
                            border-bottom: 1px solid ${COLORS.text.light};
                        }
                    `,
                    css``,
                ],
                "max",
            )}

        h3 {
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            font-size: ${TYPOGRAPHY.size.tiny};
                            width: 100%;
                            padding: 1em 0;
                            font-size: ${TYPOGRAPHY.size.base};
                            text-transform: none;
                        `,
                        css``,
                        css`
                            padding: 0;
                            margin-bottom: ${TYPOGRAPHY.size.base};
                            font-size: ${TYPOGRAPHY.size.base};
                        `,
                    ],
                    "min",
                )}
        }

        ${StyledClickable} {
            text-transform: none;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css``,
                        css``,
                        css`
                            margin-bottom: 12px;
                        `,
                    ],
                    "max",
                )}

            ${StyledTypography} {
                ${linkStyles("white", "primary.dark")}

                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                font-size: ${TYPOGRAPHY.size.tiny};
                            `,
                            css``,
                            css`
                                font-size: ${TYPOGRAPHY.size.smallest};
                            `,
                        ],
                        "min",
                    )}
            }
        }
    `;
};

export const footerContainerStyles: FooterContainerStyles = {
    widgetGridContainer: {
        css: css`
            ${siteWidthStyles()};
            position: relative;
            background-color: ${COLORS.primary.contrast};
            padding: 48px 0;
            color: white;

            > div {
                > div {
                    position: relative;
                    z-index: 2;
                }

                &:before {
                    content: "";
                    position: absolute;
                    top: 0;
                    bottom: 0;
                    left: -9998px;
                    right: 0;
                    border-left: 9999px solid ${COLORS.primary.contrast};
                    box-shadow: 9999px 0 0 ${COLORS.primary.contrast};
                }
            }
        `,
    },
    widgetBottomGridContainer: {
        css: css`
            ${siteWidthStyles()};
            position: relative;
            background-color: black;
            color: white;

            > div {
                > div {
                    position: relative;
                    z-index: 2;
                    align-items: center;
                }

                &:before {
                    content: "";
                    position: absolute;
                    top: 0;
                    bottom: 0;
                    left: -9998px;
                    right: 0;
                    border-left: 9999px solid black;
                    box-shadow: 9999px 0 0 black;
                }
            }
        `,
    },
    // TOP
    linkList1GridItem: {
        width: [12, 12, 12, 2, 2],
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            line-height: 20px;
                            font-size: ${TYPOGRAPHY.size.tiny};

                            p {
                                font-size: ${TYPOGRAPHY.size.tiny};
                            }

                            h3 {
                                font-size: ${TYPOGRAPHY.size.tiny};
                                width: 100%;
                                padding-top: 12px;
                                padding-bottom: 20px;
                                font-size: ${TYPOGRAPHY.size.base};
                                text-transform: none;
                            }

                            ${StyledButton}, ${StyledButton} > span {
                                color: ${COLORS.primary.light};
                                font-weight: ${TYPOGRAPHY.weight.bold};
                            }

                            ${StyledButton}:hover, ${StyledButton}:hover > span {
                                color: ${COLORS.primary.dark};
                            }
                        `,
                        css``,
                        css`
                            h3 {
                                margin-bottom: 0;
                                font-size: ${TYPOGRAPHY.size.base};
                            }
                        `,
                    ],
                    "min",
                )}
        `,
    },
    linkList2GridItem: {
        width: [12, 12, 4, 2, 2],
        css: footerLinkListStyles(),
    },
    linkList3GridItem: {
        width: [12, 12, 4, 2, 2],
        css: footerLinkListStyles(),
    },
    linkList4GridItem: {
        width: [12, 12, 4, 2, 2],
        css: footerLinkListStyles(),
    },
    subscribeGridItem: {
        width: [12, 12, 12, 4, 4],
        css: css`
            display: flex;
            align-content: center;
            justify-content: flex-start;
            flex-wrap: wrap;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding-top: 24px;
                        `,
                        css``,
                        css`
                            border-top: 1px solid ${COLORS.text.light};
                        `,
                        css`
                            border-top: 0;
                        `,
                        css`
                            position: relative;
                            padding-top: 15px;
                            padding-left: 120px;
                            border-top: 0;

                            &::before {
                                content: "";
                                position: absolute;
                                top: 15px;
                                bottom: 15px;
                                left: 60px;
                                display: block;
                                width: 1px;
                                height: auto;
                                background-color: ${COLORS.text.light};
                            }
                        `,
                    ],
                    "min",
                )}
        `,
    },
    // BOTTOM
    logoGridItem: {
        width: [12, 12, 2, 2, 2],
        css: css``,
    },
    logoWrapper: {
        css: css`
            img {
                width: 138px;
                height: 42px;
            }
        `,
    },
    richContentGridItem: {
        width: [12, 12, 4, 4, 4],
        css: css`
            padding-top: 0;
            padding-bottom: 0;
            color: white;
            font-size: ${TYPOGRAPHY.size.tiny};

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding-top: 5px;
                            padding-bottom: 10px;
                        `,
                        css``,
                        css`
                            padding-top: 15px;
                            padding-bottom: 15px;
                            div {
                                font-size: ${TYPOGRAPHY.size.base};
                            }
                        `,
                    ],
                    "min",
                )}
        `,
    },
    siteMapLinksGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            padding-top: 0;
            padding-bottom: 0;
            color: white;
            font-size: ${TYPOGRAPHY.size.tiny};

            span {
                color: white;
            }

            ${StyledClickable} {
                text-transform: none;

                ${StyledTypography} {
                    ${linkStyles("white", "text.linkHover")}

                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(
                            theme,
                            [
                                css`
                                    font-size: ${TYPOGRAPHY.size.tiny};
                                `,
                                css``,
                                css`
                                    font-size: ${TYPOGRAPHY.size.base};
                                `,
                            ],
                            "min",
                        )}
                }
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            div {
                                display: block;
                                text-align: left; // Override CMS

                                a {
                                    margin-bottom: 18px;
                                }
                            }
                        `,
                        css``,
                        css`
                            div {
                                display: inline-block;
                                text-align: right; // Override CMS
                                font-size: ${TYPOGRAPHY.size.base};

                                a {
                                    margin-bottom: 0;
                                }
                            }
                        `,
                    ],
                    "min",
                )}
        `,
    },
};

// Store Custom Code - Split into 2 GridContainer
const FooterContainer: FC<OwnProps> = ({ id, extendedStyles }) => {
    const [styles] = React.useState(() => mergeToNew(footerContainerStyles, extendedStyles));
    return (
        <>
            <GridContainer {...styles.widgetGridContainer}>
                <GridItem {...styles.linkList1GridItem}>
                    <Zone zoneName="LinkList1" contentId={id} />
                </GridItem>
                <GridItem {...styles.linkList2GridItem}>
                    <Zone zoneName="LinkList2" contentId={id} />
                </GridItem>
                <GridItem {...styles.linkList3GridItem}>
                    <Zone zoneName="LinkList3" contentId={id} />
                </GridItem>
                <GridItem {...styles.linkList4GridItem}>
                    <Zone zoneName="LinkList4" contentId={id} />
                </GridItem>
                <GridItem {...styles.subscribeGridItem}>
                    <StyledWrapper css={css``}>
                        <Zone zoneName="Subscribe" contentId={id} />
                    </StyledWrapper>
                    <StyledWrapper css={css``}>
                        <Zone zoneName="SocialLinks" contentId={id} />
                    </StyledWrapper>
                </GridItem>
            </GridContainer>
            <GridContainer {...styles.widgetBottomGridContainer}>
                <GridItem {...styles.logoGridItem}>
                    <StyledWrapper {...styles.logoWrapper}>
                        <Zone zoneName="Logo" contentId={id} />
                    </StyledWrapper>
                </GridItem>
                <GridItem {...styles.richContentGridItem}>
                    <Zone zoneName="RichContent" contentId={id} />
                </GridItem>
                <GridItem {...styles.siteMapLinksGridItem}>
                    <Zone zoneName="SiteMapLinks" contentId={id} />
                </GridItem>
            </GridContainer>
        </>
    );
};

const footerContainer: WidgetModule = {
    component: FooterContainer,
    definition: {
        group: "Footer",
        allowedContexts: ["Footer"],
    },
};

export default footerContainer;
